import { createAsyncThunk } from '@reduxjs/toolkit';
import { ICompleteAction, IRegisterAction } from 'core/slices/register';
import {
  IAttemptAction,
  ICertificateAction,
  ICertificateResponse,
  IDialogueAction,
  IGroupPermissionAction,
  IGroupPermissionResponse,
  IHomeResponse,
  ILearnerForumError,
  ILearnerForumPostCreateAction,
  ILearnerForumTopicAction,
  ILearnerForumTopicCreateAction,
  ILearnerForumTopicsAction,
  IModuleAction,
  IModuleResponse,
  ISuspenseAction,
  ISystemPermissionAction,
  ISystemPermissionResponse,
  ITokenAction,
  ITokenActionResponse,
  IVerificationAction,
} from 'types/action/player';
import { ISearch, SearchType } from 'types/domain/search';

import { ISessionAttempt } from 'types/domain/session';
import { AxiosWrapper } from './axios.wrapper';
import { editLanguage, learnerLanguage, translatedModuleUrl } from './editLanguage';
import { RootState } from 'types';
import { IError } from 'types/app/error';
import { IForumPostDAO, IForumTopicDAO } from 'types/domain/forum';

export const tokenRequest = createAsyncThunk('learner/token', async (data: ITokenAction) => {
  const response = await AxiosWrapper.post('/api/learner/token', data);
  return response.data as ITokenActionResponse;
});

const forumError = (error: any): ILearnerForumError => ({
  status: error?.response?.status ?? error?.status,
  message: error?.message,
});

/**
 * Module discussion for a learner holding a course permission. Each call names
 * the course the player is running, which is what the permission is checked
 * against. Rejected with the HTTP status so a 403 can hide the discussion.
 */
export const learnerForumTopicsRequest = createAsyncThunk<
  IForumTopicDAO[],
  ILearnerForumTopicsAction,
  { rejectValue: ILearnerForumError }
>('learner/forum/topics', async ({ token, ...data }, { rejectWithValue }) => {
  try {
    const response = await AxiosWrapper.post('/api/learner/forum/topics', data);
    return response.data as IForumTopicDAO[];
  } catch (error) {
    return rejectWithValue(forumError(error));
  }
});

export const learnerForumTopicRequest = createAsyncThunk<
  IForumTopicDAO,
  ILearnerForumTopicAction,
  { rejectValue: ILearnerForumError }
>('learner/forum/topic', async ({ token, ...data }, { rejectWithValue }) => {
  try {
    const response = await AxiosWrapper.post('/api/learner/forum/topic', data);
    return response.data as IForumTopicDAO;
  } catch (error) {
    return rejectWithValue(forumError(error));
  }
});

/** A topic is only a title — send its opening message as the first post straight after. */
export const learnerForumTopicCreateRequest = createAsyncThunk<
  IForumTopicDAO,
  ILearnerForumTopicCreateAction,
  { rejectValue: ILearnerForumError }
>('learner/forum/topic/create', async ({ token, offlineId, author, ...data }, { rejectWithValue }) => {
  try {
    const response = await AxiosWrapper.post('/api/learner/forum/topic/create', data);
    return response.data as IForumTopicDAO;
  } catch (error) {
    return rejectWithValue(forumError(error));
  }
});

export const learnerForumPostCreateRequest = createAsyncThunk<
  IForumPostDAO,
  ILearnerForumPostCreateAction,
  { rejectValue: ILearnerForumError }
>(
  'learner/forum/post/create',
  async ({ token, offlineId, author, moduleId, type, ...data }, { rejectWithValue }) => {
    try {
      const response = await AxiosWrapper.post('/api/learner/forum/post/create', data);
      return response.data as IForumPostDAO;
    } catch (error) {
      return rejectWithValue(forumError(error));
    }
  },
);

export const homeRequest = createAsyncThunk(
  'learner/home',
  async (data: { courseProgressId: number; homeNodeId: number }) => {
    const response = await AxiosWrapper.post('/api/learner/home', data);
    return response.data as ITokenActionResponse;
  },
);

export const groupPermissionRequest = createAsyncThunk('learner/group', async (data: IGroupPermissionAction) => {
  const response = await AxiosWrapper.post('learner/group', data);
  return response.data as IGroupPermissionResponse;
});

export const systemPermissionRequest = createAsyncThunk('learner/system', async (data: ISystemPermissionAction) => {
  const response = await AxiosWrapper.post('learner/system', data);
  return response.data as ISystemPermissionResponse;
});

export const moduleRequest = createAsyncThunk('learner/module', async (data: IModuleAction, { getState }) => {
  const state = getState() as RootState;
  // A learner link in another language (/fr/{token}) reads each module in it.
  const language = learnerLanguage(state);

  if (data.url) {
    const translated = language ? translatedModuleUrl(data.url, language) : null;

    // Not every module has been translated into the link's language, and one
    // that hasn't has no file for it: fall back to the module as written.
    if (translated) {
      try {
        const response = await AxiosWrapper.getJson(translated, { credentials: 'include' });
        if (response?.data?.id) return response.data as IModuleResponse;
      } catch {
        // Fall through to the base file.
      }
    }

    const response = await AxiosWrapper.getJson(data.url, {
      credentials: 'include',
    });
    return response.data as IModuleResponse;
  } else {
    // The editor reads the module in the language it is translating into; a
    // learner in the link's, which the API resolves from its code.
    const languageId = editLanguage(state);
    const body = languageId ? { ...data, languageId } : language ? { ...data, languageCode: language } : data;
    const response = await AxiosWrapper.post(`/api/learner/module`, body);
    return response.data as IModuleResponse;
  }
});

export const certificateRequest = createAsyncThunk('learner/certificate', async (data: ICertificateAction) => {
  const response = await AxiosWrapper.post('/api/learner/certificate', data);
  return response.data as ICertificateResponse;
});

export const attemptRequest = createAsyncThunk('learner/attempt', async (data: IAttemptAction, { rejectWithValue }) => {
  const response = await AxiosWrapper.post('/api/learner/attempt', data);
  return response.data as ISessionAttempt;
});

export const suspenseRequest = createAsyncThunk('learner/suspense', async (data: ISuspenseAction) => {
  const response = await AxiosWrapper.post('/api/learner/suspense', data);
  return response.data;
});

export const verificationRequest = createAsyncThunk('learner/verification', async (data: IVerificationAction) => {
  const response = await AxiosWrapper.post('/api/learner/verification', data);
  return response.data;
});

export const dialogueRequest = createAsyncThunk('learner/dialogue', async (data: IDialogueAction) => {
  const response = await AxiosWrapper.post('/api/learner/dialogue', data);
  return response.data;
});

export const actionRequest = createAsyncThunk('learner/action', async (data: IRegisterAction) => {
  const response = await AxiosWrapper.post('/api/learner/action', data);
  return response.data;
});

export const completeRequest = createAsyncThunk('learner/complete', async (data: ICompleteAction) => {
  const response = await AxiosWrapper.post('/api/learner/complete', data);
  return response.data;
});

export const searchRequest = createAsyncThunk('learner/search', async (data: ISearch) => {
  const response = await AxiosWrapper.post(
    data.searchType === SearchType.LEARNER ? '/api/learner/search' : '/api/admin/search',
    data,
  );
  return response.data;
});

export const configRequest = createAsyncThunk('config', async (domain: string) => {
  const response = await AxiosWrapper.getJson(`/public/domain/${domain}/config.json`, { credentials: 'include' });
  return response.data;
});

export const errorRequest = createAsyncThunk('error', async (data: IError) => {
  const response = await AxiosWrapper.post(`/api/error`, data);
  return response.data;
});
